// Office sign-in: the sign-in screen, the role check (online, or the remembered role offline;
// admins get their token claim), the "No Role Assigned" screen, and where start-up lands.
// Moved out of app.js unchanged (docs/split-plan.md, session 7).
import { functions, waitForFirebaseReady } from './initFirebase';
import { createSignIn } from './signIn';
import { getUserRole, isAdminRole, isValidRole } from './utilities';
import { httpsCallable } from 'firebase/functions';

let shell = null;
export function setShellAuthDeps(deps) { shell = deps; }

const signInScreen = createSignIn({
  toolName: 'Office app',
  host: document.getElementById('login-modal'),
  footerHtml: 'Tech? <a href="/techs.html">Open the Techs app</a> · Dock crew? <a href="/dock.html">Open the Dock app</a>',
});

export function handleAuthStateChange(user) {
  if (!user) {
    document.getElementById('splash-screen').style.display = 'none';
    signInScreen.show();
    return;
  }
  signInScreen.hide();
  document.getElementById('splash-screen').style.display = 'none';
  verifyUserRole(user);
}
  
async function verifyUserRole(user) {
  try {
    if (!user || !user.uid) {
      console.error('User object is invalid or missing UID:', user);
      return;
    }

    const isOnline = await waitForFirebaseReady(3000);

    const cachedRole = localStorage.getItem('currentUserRole') || '';
    const cachedSecurity = localStorage.getItem('currentUserSecurity') || '';
    const cachedName = localStorage.getItem('currentUserName') || '';

    localStorage.setItem('currentUserId', user.uid);

    const nameEl = document.getElementById('currentUserName');
    const displayName = user.displayName || cachedName || '';
    if (displayName) {
      if (nameEl) nameEl.textContent = displayName;
      localStorage.setItem('currentUserName', displayName);
    }


    const withTimeout = (promise, ms, label = 'timeout') =>
      Promise.race([
        promise,
        new Promise((_, reject) => setTimeout(() => reject(new Error(label)), ms))
      ]);

    if (!isOnline) {
      const offlineRole = cachedRole || cachedSecurity;

      if (isValidRole(offlineRole)) {
        shell.userRole = offlineRole;
        localStorage.setItem('currentUserRole', offlineRole);
        if (isAdminRole(offlineRole)) localStorage.setItem('currentUserSecurity', 'admin');
        shell.offlineFlow(user);
        return;
      }

      console.error('Offline and no cached approved role. Access denied.');
      document.body.innerHTML = await noRoleText();
      const div = document.getElementById('no-role-div');
      if (div) div.style.display = 'flex';
      const logoutBtn = document.getElementById('log-out-button-no-role');
      if (logoutBtn) logoutBtn.addEventListener('click', shell.logoutUser);
      const reloadBtn = document.getElementById('reload-button-no-role');
      if (reloadBtn) reloadBtn.addEventListener('click', () => location.reload());
      return;
    }

    let role = '';
    try {
      role = await withTimeout(getUserRole(user.uid), 6000, 'Role check timeout');
    } catch (err) {
      console.error('Role lookup failed. Falling back to cached role if available:', err);
      role = cachedRole || cachedSecurity || '';
    }

    shell.userRole = role;

    if (role) {
      localStorage.setItem('currentUserRole', role);
      if (isAdminRole(role)) localStorage.setItem('currentUserSecurity', 'admin');
    }

    if (role && isAdminRole(role)) {
      try {
        const setAdminClaim = httpsCallable(functions, 'setAdminClaim');
        await withTimeout(setAdminClaim({ uid: user.uid }), 8000, 'Admin claim timeout');
        await withTimeout(user.getIdToken(true), 8000, 'Token refresh timeout');
      } catch (error) {
        console.error('Error setting admin claim:', error);
      }
    }

    if (isValidRole(role)) {
      shell.onlineFlow(user);
    } else {
      console.error('Access denied. User role is not valid.');
      document.body.innerHTML = await noRoleText();
      const div = document.getElementById('no-role-div');
      if (div) div.style.display = 'flex';
      const logoutBtn = document.getElementById('log-out-button-no-role');
      if (logoutBtn) logoutBtn.addEventListener('click', shell.logoutUser);
      const reloadBtn = document.getElementById('reload-button-no-role');
      if (reloadBtn) reloadBtn.addEventListener('click', () => location.reload());
    }
  } catch (error) {
    console.error('Error verifying user role:', error);
  }
}




    
function noRoleText(){
  return new Promise((resolve) => {
    resolve(`
    <div id="no-role-div">
    <h1>No Role Assigned</h1>
    <p>Please contact an administrator to assign a role.</p>
    <button id="reload-button-no-role">Refresh <img class="invert-color" src="/assets/reload.png" alt="Close Icon" width="24" height="24"></button>
    <button id="log-out-button-no-role">Log Out <img class="invert-color" src="/assets/log_out.png" alt="Close Icon" width="24" height="24"></button>
    </div>`);
  });
}

// True once the user has opened a page other than Work Orders from the rail. Start-up code
// that finishes late (first sync, user record) must not pull them back to Work Orders.
export function otherPageOnScreen() {
  return [...document.querySelectorAll('.page-view')]
    .some(el => el.id !== 'open-work' && el.style.display === 'flex');
}

// Start-up's "non-admins land on Operations": if the user is already elsewhere, only
// remember the view, so the Work Orders button opens on it later.
export function bootSwitchView(view) {
  if (otherPageOnScreen()) { shell.currentJobView = view; return; }
  shell.switchView(view);
}
